import { useEffect, useState, useCallback } from 'react'
import { GlowCard } from '../components/ui/GlowCard'
import { api } from '../lib/api'
import { useFeatureFlag } from '../hooks/useFeatureFlag'
import { UpgradeBanner } from '../components/ui/UpgradeBanner'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { StatusBadge } from '../components/ui/StatusBadge'
import { useToast } from '../components/ui/Toast'
import { TicketDetail } from '../components/tickets/TicketDetail'
import { TicketCreateModal } from '../components/tickets/TicketCreateModal'
import { Button } from '../components/ui/Button'
import { Select } from '../components/ui/Select'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

interface TicketListItem {
  id: string
  ticket_number: number
  subject: string
  status: string
  priority: string
  client_name: string | null
  site_name: string | null
  assigned_name: string | null
  sla_breached: number
  created_at: string
  updated_at: string
}

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'var(--err)', high: 'var(--warn)', medium: 'var(--accent-h)', low: 'var(--text-3)',
}

const STATUS_LABELS: Record<string, string> = {
  open: 'Open', pending: 'Pending', in_progress: 'In Progress',
  waiting_on_client: 'Waiting', resolved: 'Resolved', closed: 'Closed',
}

export default function Tickets() {
  const { enabled, loading: flagLoading, requiredPlan } = useFeatureFlag('tickets')
  const [tickets, setTickets] = useState<TicketListItem[]>([])
  const [stats, setStats] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [_total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterPriority, setFilterPriority] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const { toastError } = useToast()

  const loadTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      if (filterStatus) params.set('status', filterStatus)
      if (filterPriority) params.set('priority', filterPriority)
      const res = await api.get<{ data: TicketListItem[]; total: number }>(`/tickets?${params}`)
      setTickets(res.data)
      setTotal(res.total)
    } catch (err) {
      toastError('Failed to load tickets', err)
    } finally {
      setLoading(false)
    }
  }, [search, filterStatus, filterPriority, toastError])

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get<Record<string, number>>('/tickets/stats')
      setStats(res)
    } catch { /* non-critical */ }
  }, [])

  useEffect(() => {
    if (enabled) {
      loadTickets()
      loadStats()
    }
  }, [enabled, loadTickets, loadStats])

  if (flagLoading) return <TableSkeleton cols={6} />
  if (!enabled) return <UpgradeBanner feature="Ticket System" plan={requiredPlan} description="Track client issues, manage SLA compliance, and dispatch work orders from tickets." />

  if (selectedId) {
    return <TicketDetail id={selectedId} onBack={() => { setSelectedId(null); loadTickets(); loadStats() }} />
  }

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title">Tickets</h1>
        <Button icon={PlusIcon} onClick={() => setCreating(true)}>New Ticket</Button>
      </div>

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Open', value: stats.open || 0, color: 'var(--info)' },
          { label: 'Pending', value: stats.pending || 0, color: 'var(--accent)' },
          { label: 'In Progress', value: stats.in_progress || 0, color: 'var(--color-cable)' },
          { label: 'SLA Breaching', value: stats.sla_breaching || 0, color: 'var(--err)' },
        ].map((s, i) => (
          <GlowCard key={s.label} style={{ padding: '12px 20px', minWidth: 120, textAlign: 'center' }} delay={100 + i * 80}>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.label}</div>
          </GlowCard>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="text" className="input" placeholder="Search tickets..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        <div className="v3-select-wrap" style={{ width: 140 }}>
          <select className="v3-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <span className="v3-select-chevron">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6,9 12,15 18,9" />
            </svg>
          </span>
        </div>
        <Select
          value={filterPriority}
          onChange={e => setFilterPriority(e.target.value)}
          style={{ width: 120 }}
          options={[
            { value: '', label: 'All Priority' },
            { value: 'critical', label: 'Critical' },
            { value: 'high', label: 'High' },
            { value: 'medium', label: 'Medium' },
            { value: 'low', label: 'Low' },
          ]}
        />
      </div>

      {loading ? <TableSkeleton cols={6} /> : tickets.length === 0 ? (
        <EmptyState
          title="All clear — no open tickets"
          description="Create your first ticket to start tracking issues."
          action={{ label: 'New Ticket', onClick: () => setCreating(true) }}
        />
      ) : (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>#</th>
              <th>Subject</th>
              <th>Client</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Assigned</th>
              <th>Created</th>
              <th>SLA</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map(t => (
              <tr key={t.id} onClick={() => setSelectedId(t.id)} style={{ cursor: 'pointer' }}>
                <td style={{ fontWeight: 600, color: 'var(--text-secondary)', fontSize: 12 }}>#{t.ticket_number}</td>
                <td style={{ fontWeight: 600, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</td>
                <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t.client_name || '-'}</td>
                <td>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 12,
                    fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    background: PRIORITY_COLORS[t.priority] + '20', color: PRIORITY_COLORS[t.priority],
                  }}>
                    {t.priority}
                  </span>
                </td>
                <td><StatusBadge status={t.status} /></td>
                <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t.assigned_name || '-'}</td>
                <td style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{new Date(t.created_at).toLocaleDateString()}</td>
                <td>
                  {t.sla_breached ? (
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--err)' }}>BREACH</span>
                  ) : (
                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>OK</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {creating && (
        <TicketCreateModal
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); loadTickets(); loadStats() }}
        />
      )}
    </div>
  )
}
